/**
 * LOD0 Brand Image client-side prevalidation (friendly, NON-authoritative).
 *
 * The server remains the ONLY validation authority. These checks give fast,
 * kind feedback and never permit something the backend would reject; anything
 * the browser accepts still faces full server validation.
 */
export const BRAND_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const BRAND_IMAGE_MAX_DIMENSION = 2048;
export const BRAND_IMAGE_ACCEPTED_MIME = ["image/png", "image/jpeg", "image/webp"] as const;
export const BRAND_IMAGE_ACCEPT_ATTR = "image/png,image/jpeg,image/webp";

export interface PrevalidateInput {
  size: number;
  type: string;
  width?: number;
  height?: number;
}

export interface PrevalidateResult {
  ok: boolean;
  code?: string;
  message?: string;
}

const _MIME_ALIASES: Record<string, string> = { "image/jpg": "image/jpeg" };

export function prevalidateBrandImage(input: PrevalidateInput): PrevalidateResult {
  if (input.size <= 0) {
    return { ok: false, code: "BRAND_IMAGE_EMPTY", message: "The file is empty." };
  }
  if (input.size > BRAND_IMAGE_MAX_BYTES) {
    return { ok: false, code: "BRAND_IMAGE_TOO_LARGE", message: "Image must be 5 MiB or smaller." };
  }
  const mime = _MIME_ALIASES[input.type?.toLowerCase()] ?? input.type?.toLowerCase();
  if (!BRAND_IMAGE_ACCEPTED_MIME.includes(mime as (typeof BRAND_IMAGE_ACCEPTED_MIME)[number])) {
    return {
      ok: false,
      code: "BRAND_IMAGE_UNSUPPORTED_FORMAT",
      message: "Use a PNG, JPEG or WebP image.",
    };
  }
  if (input.width !== undefined && input.height !== undefined) {
    if (input.width <= 0 || input.height <= 0) {
      return { ok: false, code: "BRAND_IMAGE_INVALID_DIMENSIONS", message: "Image has no size." };
    }
    if (input.width > BRAND_IMAGE_MAX_DIMENSION || input.height > BRAND_IMAGE_MAX_DIMENSION) {
      return {
        ok: false,
        code: "BRAND_IMAGE_DIMENSIONS_TOO_LARGE",
        message: `Image must be ${BRAND_IMAGE_MAX_DIMENSION}px or less on each side.`,
      };
    }
  }
  return { ok: true };
}

/** Map any backend brand-image error code to a truthful, human message. */
export function brandImageErrorMessage(code: string, fallback?: string): string {
  switch (code) {
    case "BRAND_IMAGE_TOO_LARGE":
      return "Image is too large (5 MiB maximum).";
    case "BRAND_IMAGE_DIMENSIONS_TOO_LARGE":
      return "Image dimensions exceed 2048px on a side.";
    case "BRAND_IMAGE_INVALID_DIMENSIONS":
      return "Image dimensions are invalid.";
    case "BRAND_IMAGE_UNSUPPORTED_FORMAT":
      return "Unsupported image format. Use PNG, JPEG or WebP.";
    case "BRAND_IMAGE_UNDECODABLE":
      return "The image could not be read; it may be corrupt.";
    case "BRAND_IMAGE_MIME_MISMATCH":
      return "The file type does not match its contents.";
    case "BRAND_IMAGE_ANIMATED":
      return "Animated images are not allowed.";
    case "BRAND_IMAGE_EMPTY":
      return "The file is empty.";
    case "BRAND_IMAGE_VERSION_CONFLICT":
      return "This brand image changed elsewhere. Reloaded — please try again.";
    case "BRAND_IMAGE_TERRITORY_INELIGIBLE":
      return "This territory can no longer carry a brand image.";
    case "TERRITORY_NOT_PUBLISHABLE":
      return "Territory is below the 400 u² minimum required to publish.";
    case "BRAND_IMAGE_NOT_FOUND":
      return "There is no brand image to remove.";
    case "IDENTITY_NOT_FOUND":
      return "This territory identity is no longer active.";
    case "UNAUTHENTICATED":
      return "Please sign in with your wallet to manage the brand image.";
    case "ORIGIN_REQUIRED":
    case "UNTRUSTED_ORIGIN":
    case "CSRF_REQUIRED":
    case "CSRF_INVALID":
      return "Security check failed. Please refresh and try again.";
    case "TRANSACTION_UNAVAILABLE":
      return "Storage is temporarily unavailable. Please try again.";
    default:
      return fallback ?? "Something went wrong. Please try again.";
  }
}
